import { useContext } from 'react'

import { toast } from 'react-toastify'

import {
  Button, Form, ModalContext, SectionDivider, SidebarModal, Text,
} from '@domain-group/suit-theme'

import { deepSetObject, digObject } from '@domain-group/javascript-utils'

import useBrand, { useBrandForm } from '@hooks/useBrand'

import handleCallbackAction from '@functions/handleCallbackAction'

import ToggleListItem from '@components/ToggleListItem'

// eslint-disable-next-line max-len
import useBrandEditMediaReviewer from '@sections/Admin/packs/SystemManager/screens/Brands/screens/Edit/screens/Details/components/BrandEditMediaReviewer/hooks/useBrandEditMediaReviewer'

const MODAL_KEY = 'ManageBrandPublicLinkSettingsModal'

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageBrandPublicLinkSettingsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { brand: initBrand } = modalPayload

  const {
    callbacks: { updateBrand: updateFn },
    brand,
    updating,
  } = useBrand(initBrand)

  const {
    entityState,
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useBrandForm(brand, { validateOn: 'change' })

  const brandShowInspections = digObject(entityState, 'options.branded_public_links.show_inspections', true)
  const brandUseLightText = digObject(entityState, 'options.branded_public_links.use_light_text', true)
  const textAccentColourState = digObject(entityState, 'options.branded_public_links.text_accent_colour', '')

  const hexRegex = /^([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/

  const editMediaReviewerPayload = useBrandEditMediaReviewer({ brandId: brand.id })
  const {
    callbacks: {
      updateBrandMediaReviewerToggles,
    },
  } = editMediaReviewerPayload

  const updateAccentColour = (colour: string) => {
    const updatedState = deepSetObject(
      entityState,
      'options.branded_public_links.text_accent_colour',
      colour.trim(),
    )

    const updateBrandPayload = {
      callbacks: {
        action: updateFn,
        afterAction: closeModal,
      },
      entityParams: updatedState,
      toastText: 'Brand Public Link Accent Colour Updated',
    }

    handleCallbackAction(updateBrandPayload)
  }

  const updateBrand = () => {
    if (hexRegex.test(textAccentColourState.trim()) || !textAccentColourState.trim().length){
      updateAccentColour(textAccentColourState)
    } else if (textAccentColourState.trim().startsWith('#')){
      const sanitisedString = textAccentColourState.trim().replace(/^#/, '')
      if (hexRegex.test(sanitisedString)){
        updateAccentColour(sanitisedString)
      } else {
        toast.warn('Invalid hex colour format')
      }
    } else {
      toast.warn('Invalid hex colour format')
    }
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title="Branding"
        titleSecondLine="&amp; Disclaimers"
      />

      <SidebarModal.Content>
        <Form>
          <SectionDivider boxProps={{ marginY: 'small' }}>BRAND SETTINGS</SectionDivider>

          <Form.Field
            label="Accent Colour (#)"
            errorMessage={errors['options.branded_public_links.text_accent_colour']}
          >
            <input
              name="options.branded_public_links.text_accent_colour"
              placeholder="e.g. 1289AB"
              type="text"
              value={textAccentColourState}
              {...handlers}
            />
          </Form.Field>

          <Text
            color="bodyFontLightColor"
            fontSize="xsmall"
            lineHeight="1.3"
            marginBottom="large"
            marginTop="small"
          >
            This colour will be used for call-to-actions and other design elements on the media
            proofing page sent to the vendor.
          </Text>

          <ToggleListItem
            boxProps={{
              marginBottom: 0,
              paddingY: 'medium',
            }}
            onClick={() => updateBrandMediaReviewerToggles(
              'use_light_text',
              entityState,
              setEntityState,
            )}
            toggleOffText="No"
            toggleOnText="Yes"
            toggleValue={brandUseLightText}
          >
            <Text color="bodyFontColor">
              Use light text for all call-to-actions
            </Text>
          </ToggleListItem>

          <ToggleListItem
            boxProps={{
              marginBottom: 0,
              paddingY: 'medium',
            }}
            onClick={() => updateBrandMediaReviewerToggles(
              'show_inspections',
              entityState,
              setEntityState,
            )}
            toggleOffText="No"
            toggleOnText="Yes"
            toggleValue={brandShowInspections}
          >
            <Text color="bodyFontColor">
              Display inspection times on the vendor-facing media reviewer page
            </Text>
          </ToggleListItem>

          <SectionDivider boxProps={{ marginY: 'medium' }}>DISCLAIMERS</SectionDivider>

          <Form.Field
            label="General Disclaimer"
            errorMessage={errors['options.branded_public_links.general_disclaimer']}
          >
            {/*
             // @ts-expect-error TS2322 */}
            <textarea
              name="options.branded_public_links.general_disclaimer"
              style={{ height: 100, resize: 'vertical' }}
              value={entityState.options.branded_public_links.general_disclaimer}
              {...handlers}
            />
          </Form.Field>

          <Form.Field
            label="Legal Disclaimer"
            marginTop="large"
            errorMessage={errors['options.branded_public_links.legal_disclaimer']}
          >
            {/*
             // @ts-expect-error TS2322 */}
            <textarea
              name="options.branded_public_links.legal_disclaimer"
              style={{ height: 100, resize: 'vertical' }}
              value={entityState.options.branded_public_links.legal_disclaimer}
              {...handlers}
            />
          </Form.Field>

        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          onClick={() => updateBrand()}
          size="large"
        >
          {saveEnabled ? 'Save' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageBrandPublicLinkSettingsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
